import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../../configureStore";
import { API } from "../../../config";
import axios from "axios";

export const getCurrentDayStart = (selectedDate: Date): string => {
  const date = new Date(selectedDate);
  date.setHours(0, 0, 0, 0);

  return date.toISOString().split("T")[0];
};

export const getSelectedDayEnd = (selectedDate: Date): string => {
  const date = new Date(selectedDate);
  date.setHours(23, 59, 59, 999);

  return date.toISOString().split("T")[0];
};

export const fetchCerealSlugs = createAsyncThunk(
  "DashboardReducer/fetchCerealSlugs",
  async (_, store) => {
    const state = store.getState() as RootState;
    const { user } = state.Authentication;
    const { locale, localizedTextsCodes } = state.LayoutReducer;
    const { cropsSelected } = state.DashboardReducer;

    const getCodeForLocale = (locale: string) => {
      const mapping: { [key: string]: number } = localizedTextsCodes;
      return mapping[locale] || 12;
    };

    if (cropsSelected.length > 0) {
      return cropsSelected;
    } else {
      try {
        const response = await axios.get(
          `${API}/core/text/?$joinRelation=localizedTexts&$select[]=slug&$select[]=localizedTexts.translation&originTableName=core.cereal_type&localizedTexts.localeConstantId=${getCodeForLocale(
            locale,
          )}`,
          {
            headers: {
              "Accept-Language": locale,
              Authorization: `Bearer ${user!.token}`,
            },
          },
        );

        const { data } = response;

        const newResponse = data.data.map((item: any) => ({
          slug: item.slug,
          translation: item.translation ? item.translation : item.slug,
          id: item.id,
        }));

        return newResponse;
      } catch (error: any) {
        return error;
      }
    }
  },
);

export const fetchCropInfo = createAsyncThunk(
  "DashboardReducer/fetchCropInfo",
  async (cropSlug: string, store) => {
    const state = store.getState() as RootState;
    const { user } = state.Authentication;
    const { locale } = state.LayoutReducer;
    const { dataDateActivity, locations } = (store.getState() as RootState)
      .DashboardFiltersReducer;

    const fromDate = getCurrentDayStart(dataDateActivity[0]);
    const toDate = getSelectedDayEnd(new Date(dataDateActivity[1]));

    const geoPointIds = locations.valuesSelected.map((loc) => loc.id);
    const geoPointParams = geoPointIds.map((id) => `id[$in][]=${id}`).join("&");

    const params = [
      `actionsPerVar[]=${fromDate}`,
      `actionsPerVar[]=${toDate}`,
      `avgProtPerVar[]=${fromDate}`,
      `avgProtPerVar[]=${toDate}`,
      `specie=${cropSlug}`,
    ].join("&");

    try {
      const response = await axios.get(
        `${API}/users/organization-geo-point?${geoPointParams}&${params}`,
        {
          headers: {
            Authorization: `Bearer ${user!.token}`,
            "Accept-Language": `${locale}`,
          },
        },
      );
      const { data } = response;

      const filterData = data.data.filter(
        (item: { actionsPerVar: { variety: string; actions: number }[] }) =>
          item.actionsPerVar !== undefined,
      );
      const filterDataProtein = data.data.filter(
        (item: { avgProtPerVar: { variety: string; actions: number }[] }) =>
          item.avgProtPerVar !== undefined,
      );

      const actionsPervariety = filterData.flatMap(
        (item) => item.actionsPerVar,
      );

      const avgProteinPerVariety = filterDataProtein.flatMap(
        (item) => item.avgProtPerVar,
      );

      const CreateArrayAverageForAvgChart = Object.values(
        avgProteinPerVariety.reduce(
          (
            acc: Record<
              string,
              { variety: string; sum: number; count: number }
            >,
            { average, variety },
          ) => {
            if (!acc[variety]) {
              acc[variety] = { variety, sum: 0, count: 0 };
            }
            acc[variety].sum += average;
            acc[variety].count += 1;
            return acc;
          },
          {},
        ),
      ).map((item: any) => {
        const average = item.sum / item.count;
        return {
          variety: item.variety,
          average: Number.parseFloat(average.toFixed(3)),
        };
      });

      const actionsPerVarietyGroup = actionsPervariety.reduce(
        (
          acc: { [key: string]: number },
          action: { variety: string; actions: number },
        ) => {
          const variety = action.variety;
          if (acc[variety]) {
            acc[variety] += action.actions;
          } else {
            acc[variety] = action.actions;
          }
          return acc;
        },
        {},
      );

      const sumActions = actionsPervariety.reduce((sum: number, item: any) => {
        return sum + item.actions;
      }, 0);

      const createArrayForCharts = Object.entries(actionsPerVarietyGroup).map(
        ([variety, actions]) => ({
          variety,
          actions,
        }),
      );

      return {
        createArrayForCharts,
        sumActions,
        CreateArrayAverageForAvgChart,
      };
    } catch (error) {
      return error;
    }
  },
);
